import type { FC } from "react";
import { useState } from "react";

import { useViewport } from "@ui/hooks/useViewport";
import AppleWallet from "@ui/images/add_to_apple_wallet.svg";
import GooglePay from "@ui/images/add_to_google_pay.svg";
import { useTranslations } from "@ui/providers/useTranslations";
import { getBackendErrorCode } from "@ui/utils/backendError";
import { createWalletUrl } from "@ui/utils/createWalletUrl";

import { useConfig } from "@jti/app-context/useConfig";
import { toast } from "@jti/core-components/components/Toaster";
import type { WalletServiceType } from "@jti/fe-common";
import { BREAKPOINT_SMALL_LARGE, translateBackendErrorCode } from "@jti/fe-common";
import { CasProfileApi } from "@jti/rest-api";

import styles from "./AddToWallet.module.scss";

type AddToWalletProps = {
  walletService: WalletServiceType;
};

export const AddToWallet: FC<AddToWalletProps> = ({ walletService }) => {
  const [loading, setLoading] = useState(false);
  const { ANDROID_IMG, APPLE_IMG, CAS_URL } = useConfig();
  const { width } = useViewport();
  const { __, dictionary } = useTranslations();

  const isMobile = width <= BREAKPOINT_SMALL_LARGE;
  const isAppleWallet = walletService === "apple";
  const [WalletSvgImage, externalImage] = isAppleWallet ? [AppleWallet, APPLE_IMG] : [GooglePay, ANDROID_IMG];
  const label = isAppleWallet ? __("libs.ui.add_to_wallet.apple.label") : __("libs.ui.add_to_wallet.google.label");
  const isValidExternalImage = externalImage?.trim();

  const requestWalletUrl = async () => {
    if (!walletService) return;
    setLoading(true);
    try {
      const { data } = await CasProfileApi.init().requestWalletUrl(walletService);
      if (data.pass_url) {
        const finalUrl = isAppleWallet ? createWalletUrl(CAS_URL, data.pass_url).toString() : data.pass_url;
        window.location.href = finalUrl;
      } else {
        toast.error(__("libs.ui.add_to_wallet.error_message"));
      }
    } catch (error) {
      toast.error(
        translateBackendErrorCode(getBackendErrorCode(error), __("libs.ui.add_to_wallet.error_message"), dictionary)
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isMobile || !walletService) return null;

  return (
    <button
      type="button"
      onClick={requestWalletUrl}
      disabled={loading}
      aria-disabled={loading}
      aria-label={label}
      className={styles.walletButton}
      data-testid="addToWallet"
    >
      {isValidExternalImage ? (
        <img src={externalImage} alt={label} className={styles.walletImage} />
      ) : (
        <WalletSvgImage />
      )}
    </button>
  );
};
